// Список клавиш `?` (contracts/ui.md; ux.md, раздел 8): один и полный — всё, что работает
// у этого пользователя, в нём есть. Клавиши одного шага — рядом, шаги — «затем», другие клавиши
// того же действия — «или». Тот же список — в разделе настроек «Клавиши» (007).
import { Button, Kbd, Modal, ModalFooter } from '@kp/ui';
import { Fragment } from 'react';
import { useShell } from './context.tsx';
import { useCreateItems } from './create.tsx';
import { shortcutRows } from './keys.ts';
import type { ShortcutRow } from './keys.ts';
import { can, canPaletteSearch, sections } from './permissions.ts';
import { useMe } from './session.ts';

/** Клавиши действия: «Ctrl K», «G затем H». */
function Steps({ steps }: { steps: ShortcutRow['steps'] }) {
  return steps.map((step, index) => (
    <Fragment key={step.join('+')}>
      {index > 0 ? <span className="kp-keys-then">затем</span> : null}
      <span className="kp-keys-step">
        {step.map((key) => (
          <Kbd key={key}>{key}</Kbd>
        ))}
      </span>
    </Fragment>
  ));
}

/** Клавиши, которые работают у этого пользователя. */
function useShortcutRows(): ShortcutRow[] {
  const me = useMe();
  const createItems = useCreateItems();
  return shortcutRows({
    canCreate: createItems.length > 0,
    canUsers: can(me.permissions, sections.users.operations),
    canAudit: can(me.permissions, sections.audit.operations),
    canSearch: canPaletteSearch(me.permissions),
    canLog: can(me.permissions, sections.log.operations),
    canStatus: can(me.permissions, sections.status.operations),
    canJobs: can(me.permissions, sections.jobs.operations),
    canDocuments: can(me.permissions, sections.documents.operations),
    // 032 (FR-002): «Входящие» — у каждого с учётной записью.
    canInbox: true,
  });
}

/** Список клавиш: клавиши слева, действие справа. */
export function ShortcutsList() {
  const rows = useShortcutRows();
  return (
    <dl className="kp-keys">
      {rows.map((row) => (
        <div key={row.label}>
          <dt>
            <Steps steps={row.steps} />
            {row.or?.map((steps) => (
              <Fragment key={steps.map((step) => step.join('+')).join(' ')}>
                <span className="kp-keys-then">или</span>
                <Steps steps={steps} />
              </Fragment>
            ))}
          </dt>
          <dd>{row.label}</dd>
        </div>
      ))}
    </dl>
  );
}

export function ShortcutsDialog() {
  const { shortcutsOpen, setShortcutsOpen } = useShell();
  return (
    <Modal
      open={shortcutsOpen}
      onOpenChange={setShortcutsOpen}
      title="Список клавиш"
      description="Однобуквенные клавиши не работают, пока открыто окно или идёт ввод текста"
      footer={
        <ModalFooter
          cancelLabel={null}
          primary={
            // Фокус — на «Готово»: Enter и Esc сразу закрывают список.
            <Button variant="primary" data-autofocus onClick={() => setShortcutsOpen(false)}>
              Готово
            </Button>
          }
        />
      }
    >
      <ShortcutsList />
    </Modal>
  );
}
